import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { SignupPayload } from "@/app/auth/signup/signup.entity.ts";
import { refusedByTheApi } from "@shyne/shared/api";
import { inviteOrgSignUp, newSignUp, readInvite } from "@/app/auth/signup/signup.api.ts";
import { fetchUser } from "@/lib/store/auth.slice.ts";
import { fetchOrganizations } from "@/lib/store/organization.slice.ts";
import supabase from "@/lib/supabase.ts";
import { fetchMembership } from "@/lib/store/membership.slice.ts";
import { resetWizard, setOrgInvite } from "@/app/auth/signup/signup.components/wizard/wizard.slice.ts";
import { toast } from "sonner";

interface SignupState {
  saving: boolean;
  error: string | null;
  needsVerification: boolean;
  inviteInitLoading: boolean;
  inviteInitError: string | null;
}

const initialState: SignupState = {
  saving: false,
  error: null,
  needsVerification: false,
  inviteInitLoading: false,
  inviteInitError: null,
};

export const signupUser = createAsyncThunk(
  "signup/signupUser",
  async (
    { path, payload, token }: { path: "plain" | "orgInvite"; payload: SignupPayload; token?: string },
    { dispatch }
  ) => {
    const res = await (path === "orgInvite" ? inviteOrgSignUp(token!, payload) : newSignUp(payload));
    const needsVerification = !(await supabase.auth.getSession()).data?.session;

    await Promise.all([dispatch(fetchUser()), dispatch(fetchOrganizations()), dispatch(fetchMembership())]);

    return { res, needsVerification };
  }
);

type InviteReadState = { signup: { inviteInitLoading: boolean }; signupWizard: { inviteToken: string | null } };

export const initSignupFromInvite = createAsyncThunk<void, string, { dispatch: any }>(
  "signup/initSignupFromInvite",
  async (token, { dispatch, rejectWithValue }) => {
    try {
      const invite = await readInvite(token);

      if (invite.kind === "vendorRelationship") {
        throw new Error("This invitation is answered on its vendor relationship page. Open the link in the email you were sent.");
      }

      dispatch(resetWizard());
      dispatch(setOrgInvite({ token, invite }));
    } catch (err: unknown) {
      const message = refusedByTheApi(err) && err.status === 404 ? "This invitation is no longer valid." : err instanceof Error ? err.message : "Unable to load invite.";
      toast.error(message);
      return rejectWithValue(message);
    }
  },
  {
    condition: (token, { getState }) => {
      const { signup, signupWizard } = getState() as InviteReadState;
      return !signup.inviteInitLoading && signupWizard.inviteToken !== token;
    },
  }
);

export const refreshSignedIn = createAsyncThunk("signup/refreshSignedIn", async (_: void, { dispatch }) => {
  await Promise.all([dispatch(fetchUser()), dispatch(fetchOrganizations()), dispatch(fetchMembership())]);
});

export const settleIntoOrganization = createAsyncThunk("signup/settleIntoOrganization", async (organization: string, { dispatch }) => {
  localStorage.setItem("active_org_id", organization);
  await dispatch(refreshSignedIn());
});

const slice = createSlice({
  name: "signup",
  initialState,
  reducers: {},
  extraReducers: (b) =>
    b
      .addCase(signupUser.pending, (s) => {
        s.saving = true;
        s.error = null;
        s.needsVerification = false;
      })
      .addCase(signupUser.fulfilled, (s, a) => {
        s.saving = false;
        s.needsVerification = a.payload.needsVerification ?? true;
      })
      .addCase(signupUser.rejected, (s, a) => {
        s.saving = false;
        s.error = a.error.message ?? "Signup failed";
      })
      .addCase(initSignupFromInvite.pending, (s) => {
        s.inviteInitLoading = true;
        s.inviteInitError = null;
      })
      .addCase(initSignupFromInvite.fulfilled, (s) => {
        s.inviteInitLoading = false;
      })
      .addCase(initSignupFromInvite.rejected, (s, a) => {
        s.inviteInitLoading = false;
        s.inviteInitError = (a.payload as string) ?? a.error.message ?? "Invitation could not be processed";
      }),
});

const signupReducer = slice.reducer;

export default signupReducer;
